import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Send the access_token HttpOnly cookie on every request (P5 — 22 May 2026).
// Session storage Bearer header is kept as fallback during the migration period.
axios.defaults.withCredentials = true;

export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    // Always try checkAuth — the HttpOnly cookie may carry a valid session
    // even without a sessionStorage token (e.g. after page reload).
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // 401 is a normal "not logged in" state — just clear any stale token.
      sessionStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token, user_id, username: userName, role } = response.data;
      sessionStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ user_id, username: userName, role });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Errore di login'
      };
    }
  };

  const loginCustomer = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/customer/login`, {
        email,
        password,
        remember_me: !!rememberMe,
      });
      const { access_token, user_id, username: userName, role } = response.data;
      // sessionStorage stays as the Bearer-header fallback for the migration
      // period (P5). The HttpOnly cookie set by the backend is the primary
      // source of truth — and gets the extended 30-day Max-Age when remember_me=true.
      sessionStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ user_id, username: userName, role });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Errore di accesso' };
    }
  };

  const registerCustomer = async (email, password, full_name, phone, referrer_code = null, codice_fiscale = null, address_info = null) => {
    try {
      const body = { email, password, full_name, phone };
      try {
        let did = localStorage.getItem("evr_device_id");
        if (!did) {
          did = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
          localStorage.setItem("evr_device_id", did);
        }
        body.device_id = did;
      } catch (e) { /* storage disabled: skip */ }
      if (referrer_code) body.referrer_code = referrer_code;
      if (codice_fiscale) body.codice_fiscale = codice_fiscale;
      if (address_info) {
        if (address_info.country) body.country = address_info.country;
        if (address_info.address) body.address = address_info.address;
        if (address_info.city) body.city = address_info.city;
        if (address_info.postal_code) body.postal_code = address_info.postal_code;
      }
      const response = await axios.post(`${API}/customer/register`, body);
      const { access_token, user_id, username: userName, role } = response.data;
      sessionStorage.setItem('access_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser({ user_id, username: userName, role });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Errore di registrazione' };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const r = await axios.post(`${API}/customer/password-reset/request`, { email });
      return { success: true, message: r.data?.message || 'Email inviata' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Errore richiesta reset' };
    }
  };

  const confirmPasswordReset = async (token, new_password) => {
    try {
      const r = await axios.post(`${API}/customer/password-reset/confirm`, { token, new_password });
      return { success: true, message: r.data?.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Errore reset password' };
    }
  };

  const logout = async () => {
    try {
      // Clear the HttpOnly cookie server-side (P5 — 22 May 2026).
      await axios.post(`${API}/customer/logout`);
    } catch {
      // Network errors are non-blocking — we still clear client-side state.
    }
    sessionStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Memoize context value to avoid recreating object on every render
  // (otherwise every consumer would re-render whenever this provider does)
  const contextValue = useMemo(
    () => ({ user, login, loginCustomer, registerCustomer, requestPasswordReset, confirmPasswordReset, logout, loading }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
